import { csrfTokenManager } from '@/lib/security/csrf/token-manager';
import type { Database } from '@/lib/supabase/types';
import { logger } from '@/utils/logger';

type Review = Database['public']['Tables']['reviews']['Row'];

export interface ReviewSubmission {
  name: string;
  email: string;
  rating: number;
  review_text: string;
}

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  message?: string;
  errors?: Record<string, string>;
}

export const reviewsService = {
  async submitReview(review: ReviewSubmission): Promise<ApiResponse<Review>> {
    try {
      // Get CSRF token
      const csrfToken = await csrfTokenManager.getToken();
      
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': csrfToken,
        },
        body: JSON.stringify(review),
      });

      const result: ApiResponse<Review> = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        
        if (response.status === 400 && result.errors) {
          const errorMessage = Object.values(result.errors).join('\n');
          throw new Error(errorMessage || 'Validation failed');
        }
        
        throw new Error(result.error || 'Failed to submit review');
      }

      return { data: result.data, error: null };
    } catch (error) {
      logger.error('Error submitting review', { error });
      return { 
        data: null, 
        error: error instanceof Error ? error.message : 'An unknown error occurred' 
      };
    }
  },

  async getApprovedReviews(): Promise<{ data: Review[] | null; error: string | null }> {
    try {
      const res = await fetch('/api/reviews', { method: 'GET' });
      if (!res.ok) {
        return { data: null, error: `Failed to fetch reviews (${res.status})` };
      }
      const json = await res.json();
      return { data: (json?.data as Review[]) ?? [], error: null };
    } catch (error) {
      logger.error('Error fetching reviews', { error });
      return { 
        data: null, 
        error: error instanceof Error ? error.message : 'Failed to fetch reviews' 
      };
    }
  }
};